
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { TRAIT_CLASS_RANGES, Trait } from "@/lib/consts";
import { CompetencyStatus } from "@/lib/competencies";
import { avgFor, CompetencyStat, displayName, LeaderSummary, PRIORITY_KEYS, PRIORITY_LEVELS, PRIORITY_ORDER, PriorityKey, ReportFilters, Subgroup } from "./shared";

interface SpreadBubble {
  leader: LeaderSummary;
  score: number;
  status: CompetencyStatus;
}

interface SpreadCluster {
  items: SpreadBubble[];
  centre: number;
}

// Same overlap handling as the evaluation report's CompetencySpread:
// scores whose whole span is within TIE_EPS collapse into one tied count dot; anything else stays an individual bubble.
const TIE_EPS = 1.5;

const LEVEL_KEYS: Record<CompetencyStatus, string> = { "highly-prioritized": "hp", prioritized: "p", neutral: "lp" };
const STATUS_LITERALS: Record<CompetencyStatus, string> = { "highly-prioritized": "Legend::HighlyPrioritized", prioritized: "Legend::Prioritized", neutral: "Legend::LessPrioritized" };

@Component
export default class TeamCompetencySpread extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true }) readonly stats!: CompetencyStat[];
  @Prop({ required: true }) readonly visibleLeaders!: LeaderSummary[];
  @Prop({ required: true }) readonly subgroups!: Subgroup[];
  @Prop({ required: true }) readonly filters!: ReportFilters;
  @Prop({ default: false }) readonly printing!: boolean;

  public openGroups: Record<PriorityKey, boolean> = { critical: true, important: false, supporting: false };
  public tooltip: { visible: boolean; x: number; y: number; name: string; levelText: string; levelKey: string; rows: Array<{ name: string; color: string; levelText: string; levelKey: string }> } = {
    visible: false,
    x: 0,
    y: 0,
    name: "",
    levelText: "",
    levelKey: "p",
    rows: [],
  };

  get groups() {
    return PRIORITY_ORDER.map((key) => ({ key, stats: this.stats.filter((s) => s.level === PRIORITY_LEVELS[key]) })).filter((g) => g.stats.length > 0);
  }

  isOpen(key: PriorityKey): boolean {
    return this.printing || this.openGroups[key];
  }

  toggleGroup(key: PriorityKey): void {
    this.$set(this.openGroups, key, !this.openGroups[key]);
  }

  benchLow(trait: string): number {
    return TRAIT_CLASS_RANGES[trait as Trait]?.low ?? 40;
  }

  benchWidth(trait: string): number {
    const range = TRAIT_CLASS_RANGES[trait as Trait];
    return range ? range.high - range.low : 20;
  }

  nameOf(leader: LeaderSummary): string {
    return displayName(leader, this.filters.hideNames);
  }

  initialsOf(leader: LeaderSummary): string {
    return this.filters.hideNames ? leader.label : leader.initials;
  }

  hasScore(leader: LeaderSummary, trait: string): boolean {
    const s = leader.traitScores[trait];
    return typeof s === "number" && !Number.isNaN(s);
  }

  subgroupMarkers(trait: string): Array<{ id: string; label: string; color: string; avg: number }> {
    return this.subgroups
      .filter((sg) => this.filters.activeSubgroupIds.includes(sg.id))
      .map((sg) => {
        const members = this.visibleLeaders.filter((l) => sg.memberIds.includes(l.id) && this.hasScore(l, trait));
        return { id: sg.id, label: sg.label, color: sg.color, avg: members.length ? avgFor(trait, members) : NaN };
      })
      .filter((m) => !Number.isNaN(m.avg));
  }

  // Lowest scores first so higher scores render on top when bubbles overlap.
  bubbles(trait: string): SpreadBubble[] {
    return this.visibleLeaders
      .filter((l) => this.hasScore(l, trait))
      .map((leader) => ({ leader, score: leader.traitScores[trait], status: leader.traitStatus[trait] ?? ("neutral" as CompetencyStatus) }))
      .sort((a, b) => a.score - b.score);
  }

  clusters(trait: string): SpreadCluster[] {
    const out: SpreadBubble[][] = [];
    this.bubbles(trait).forEach((item) => {
      const last = out[out.length - 1];
      if (last && item.score - last[0].score <= TIE_EPS) last.push(item);
      else out.push([item]);
    });
    return out.map((items) => ({
      items,
      centre: items.reduce((sum, x) => sum + x.score, 0) / items.length,
    }));
  }

  statusLabel(status: CompetencyStatus): string {
    return this.$t(STATUS_LITERALS[status]).toString();
  }

  showTooltip(event: MouseEvent, item: SpreadBubble): void {
    this.tooltip = { visible: true, x: event.clientX + 14, y: event.clientY - 10, name: this.nameOf(item.leader), levelText: this.statusLabel(item.status), levelKey: LEVEL_KEYS[item.status], rows: [] };
  }

  showGroupTooltip(event: MouseEvent, cluster: SpreadCluster): void {
    const head = this.$t("Spread::Tooltip::Tied", { n: cluster.items.length }).toString();
    this.tooltip = {
      visible: true,
      x: event.clientX + 14,
      y: event.clientY - 10,
      name: head,
      levelText: "",
      levelKey: "p",
      rows: cluster.items.map((item) => ({ name: this.nameOf(item.leader), color: item.leader.color, levelText: this.statusLabel(item.status), levelKey: LEVEL_KEYS[item.status] })),
    };
  }

  showSimpleTooltip(event: MouseEvent, name: string): void {
    this.tooltip = { visible: true, x: event.clientX + 14, y: event.clientY - 10, name, levelText: "", levelKey: "p", rows: [] };
  }

  moveTooltip(event: MouseEvent): void {
    this.tooltip.x = event.clientX + 14;
    this.tooltip.y = event.clientY - 10;
  }

  hideTooltip(): void {
    this.tooltip.visible = false;
  }

  priorityKey(level: 0 | 1 | 2): PriorityKey {
    return PRIORITY_KEYS[level];
  }
}
